"use client";

import { useState } from "react";

interface ServiceVariant {
  id: number;
  service_type_id?: number;
  name: string;
  price: number;
}

interface ServiceItem {
  id: number;
  name: string;
  image_url?: string;
  variants: ServiceVariant[];
}

interface ServiceGridProps {
  loadingServices: boolean;
  services: ServiceItem[];
  searchTerm: string;
  addToOrder: (service: ServiceItem, variant: ServiceVariant) => void;
}

export default function ServiceGrid({
  loadingServices,
  services,
  searchTerm,
  addToOrder,
}: ServiceGridProps) {
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(
    null
  );

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filtered = services.filter((svc) =>
    svc.name.toLowerCase().includes(normalizedSearch)
  );

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border-2 border-gray-300">
      <h3 className="font-semibold text-gray-700 mb-4 text-lg">Sản phẩm</h3>

      {loadingServices ? (
        <div className="text-center text-gray-400 py-8">Đang tải dịch vụ...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          Không có dịch vụ phù hợp
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-4">
          {filtered.map((svc) => (
            <div
              key={svc.id}
              onClick={() => setSelectedService(svc)}
              className="border rounded-xl bg-white shadow-sm transition flex flex-col items-center cursor-pointer hover:shadow-lg hover:border-blue-400"
            >
              {/* Hình ảnh */}
              <div className="bg-slate-100 rounded-full p-3 mb-2 hover:bg-blue-100 transition mt-3">
                <img
                  src={
                    svc.image_url ||
                    "https://cdn-icons-png.flaticon.com/512/11329/11329060.png"
                  }
                  alt={svc.name}
                  className="w-10 h-10 object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://cdn-icons-png.flaticon.com/512/11329/11329060.png";
                  }}
                />
              </div>
              <div className="text-center font-semibold text-gray-700 text-sm mb-3">
                {svc.name}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* === POPUP MODAL === */}
      {selectedService && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[400px] max-w-[50vw] shadow-2xl animate-fadeIn mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="text-lg font-semibold text-green-700">
                {selectedService.name}
              </h3>
              <button
                onClick={() => setSelectedService(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Nội dung */}
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              <div className="flex flex-col gap-2">
                {selectedService.variants.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => addToOrder(selectedService, variant)}
                    className="flex justify-between items-center border rounded-lg px-4 py-2 text-sm bg-blue-50 hover:bg-blue-100 active:bg-blue-200 transition"
                  >
                    <span>{variant.name}</span>
                    <span className="font-medium text-blue-600">
                      {variant.price.toLocaleString("vi-VN")}₫
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t p-4 text-center">
              <button
                onClick={() => setSelectedService(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 transition text-green-600"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
